import React, { useState, useRef, useEffect } from 'react'
import { Trash2 } from 'lucide-react'
import { cn } from '../../lib/utils'

interface SwipeableCardProps {
  children: React.ReactNode
  onRemove?: () => void
  disabled?: boolean
  className?: string
}

export function SwipeableCard({
  children,
  onRemove,
  disabled = false,
  className
}: SwipeableCardProps) {
  const [translateX, setTranslateX] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [showDelete, setShowDelete] = useState(false)
  const startXRef = useRef(0)
  const currentXRef = useRef(0)
  const cardRef = useRef<HTMLDivElement>(null)

  const SWIPE_THRESHOLD = 80 // pixels to swipe before showing delete

  const handleTouchStart = (e: React.TouchEvent) => {
    if (disabled || !onRemove) return
    setIsDragging(true)
    startXRef.current = e.touches[0].clientX
    currentXRef.current = translateX
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || disabled || !onRemove) return
    const currentX = e.touches[0].clientX
    const diff = currentX - startXRef.current
    const newTranslateX = currentXRef.current + diff

    // Only allow left swipe (negative values)
    if (newTranslateX < 0) {
      setTranslateX(newTranslateX)
      setShowDelete(Math.abs(newTranslateX) > SWIPE_THRESHOLD)
    }
  }

  const handleTouchEnd = () => {
    if (!isDragging || disabled || !onRemove) return
    setIsDragging(false)

    if (Math.abs(translateX) > SWIPE_THRESHOLD) {
      // Swipe threshold reached, show delete button
      setTranslateX(-100)
      setShowDelete(true)
    } else {
      // Reset position
      setTranslateX(0)
      setShowDelete(false)
    }
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    if (disabled || !onRemove) return
    setIsDragging(true)
    startXRef.current = e.clientX
    currentXRef.current = translateX
  }

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging || disabled || !onRemove) return
    const diff = e.clientX - startXRef.current
    const newTranslateX = currentXRef.current + diff

    // Only allow left swipe (negative values)
    if (newTranslateX < 0) {
      setTranslateX(newTranslateX)
      setShowDelete(Math.abs(newTranslateX) > SWIPE_THRESHOLD)
    }
  }

  const handleMouseUp = () => {
    if (!isDragging || disabled || !onRemove) return
    setIsDragging(false)

    if (Math.abs(translateX) > SWIPE_THRESHOLD) {
      setTranslateX(-100)
      setShowDelete(true)
    } else {
      setTranslateX(0)
      setShowDelete(false)
    }
  }

  const handleDelete = () => {
    if (onRemove) {
      onRemove()
      // Reset state after deletion
      setTimeout(() => {
        setTranslateX(0)
        setShowDelete(false)
      }, 300)
    }
  }

  const handleReset = () => {
    setTranslateX(0)
    setShowDelete(false)
  }

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [isDragging])

  // Reset on disabled change
  useEffect(() => {
    if (disabled) {
      handleReset()
    }
  }, [disabled])

  if (!onRemove || disabled) {
    return <div className={className}>{children}</div>
  }

  return (
    <div className={cn('relative overflow-hidden', className)} ref={cardRef}>
      {/* Delete action background */}
      <div
        className={cn(
          'absolute right-0 top-0 bottom-0 bg-red-500 flex items-center justify-center px-6 transition-opacity',
          showDelete ? 'opacity-100' : 'opacity-0'
        )}
        style={{ width: '100px' }}
      >
        <button
          onClick={handleDelete}
          className="text-white flex flex-col items-center justify-center min-h-[48px] min-w-[48px] touch-manipulation active:scale-95 transition-transform"
          aria-label="Delete team"
        >
          <Trash2 className="h-6 w-6" />
          <span className="text-xs mt-1">Delete</span>
        </button>
      </div>

      {/* Swipeable card content */}
      <div
        className={cn(
          'transition-transform bg-white relative z-10',
          isDragging ? 'transition-none' : 'duration-300 ease-out'
        )}
        style={{
          transform: `translateX(${translateX}px)`,
          cursor: isDragging ? 'grabbing' : 'grab'
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onClick={showDelete ? handleReset : undefined}
      >
        {children}
      </div>

      {/* Hint indicator - only show on first render */}
      {!showDelete && translateX === 0 && (
        <div className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none tablet:hidden">
          <div className="flex items-center space-x-1 text-xs">
            <span>Swipe</span>
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </div>
        </div>
      )}
    </div>
  )
}
